'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';

import { useTranslations } from '@/components/language-provider';
import { useTheme } from '@/components/theme-provider';
import { buildCourtIntelligenceShareMailto } from '@/lib/court-intelligence-marketing';

const SETUP_GUIDE_FALLBACK = {
  title: '',
  subtitle: '',
  openGuideLabel: '',
  eyebrowLabel: '',
  descriptionLabel: '',
  outcomes: {
    quickEstimate: {
      label: '',
      eyebrow: '',
      description: '',
      unlocksTitle: '',
      unlocks: [] as string[],
      setupTitle: '',
      setup: [] as string[],
    },
    courtAvailability: {
      label: '',
      eyebrow: '',
      description: '',
      unlocksTitle: '',
      unlocks: [] as string[],
      setupTitle: '',
      setup: [] as string[],
    },
    courtAndQueue: {
      label: '',
      eyebrow: '',
      description: '',
      unlocksTitle: '',
      unlocks: [] as string[],
      setupTitle: '',
      setup: [] as string[],
    },
  },
  modal: {
    title: '',
    closeLabel: '',
  },
};

function CheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" {...props}>
      <path d="M4 10.5 8.5 15 16 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function CourtIntelligencePage() {
  const { messages } = useTranslations();
  const { actualTheme } = useTheme();
  const content = messages.courtIntelligence;
  const setupGuide = content.setupGuide ?? SETUP_GUIDE_FALLBACK;
  const playerAdvocacy = content.playerAdvocacy;
  const courtIntelLogoSrc = actualTheme === 'dark' ? '/images/court-intelligence-logo-dark.svg' : '/images/court-intelligence-logo.svg';
  const [setupTarget, setSetupTarget] = useState<'quick_estimate' | 'court_availability' | 'court_and_queue'>(
    'quick_estimate',
  );
  const [activeGuidanceIndex, setActiveGuidanceIndex] = useState(0);
  const [guideOpen, setGuideOpen] = useState(false);

  const selectedOutcome = useMemo(() => {
    switch (setupTarget) {
      case 'court_availability':
        return setupGuide.outcomes.courtAvailability;
      case 'court_and_queue':
        return setupGuide.outcomes.courtAndQueue;
      default:
        return setupGuide.outcomes.quickEstimate;
    }
  }, [setupGuide.outcomes.courtAndQueue, setupGuide.outcomes.courtAvailability, setupGuide.outcomes.quickEstimate, setupTarget]);

  const setupPanels = useMemo(
    () => [
      { key: 'eyebrow', label: setupGuide.eyebrowLabel, value: selectedOutcome.eyebrow },
      { key: 'description', label: setupGuide.descriptionLabel, value: selectedOutcome.description },
      { key: 'unlocks', label: selectedOutcome.unlocksTitle, value: selectedOutcome.unlocks.join(' • ') },
      { key: 'setup', label: selectedOutcome.setupTitle, value: selectedOutcome.setup.join(' • ') },
    ],
    [
      selectedOutcome.description,
      selectedOutcome.eyebrow,
      selectedOutcome.setup,
      selectedOutcome.setupTitle,
      selectedOutcome.unlocks,
      selectedOutcome.unlocksTitle,
      setupGuide.descriptionLabel,
      setupGuide.eyebrowLabel,
    ],
  );

  const activePanel = setupPanels[activeGuidanceIndex] ?? setupPanels[0] ?? null;
  const playerAdvocacyHref = useMemo(
    () => buildCourtIntelligenceShareMailto(playerAdvocacy.shareSubject, playerAdvocacy.shareBody),
    [playerAdvocacy.shareBody, playerAdvocacy.shareSubject],
  );

  useEffect(() => {
    setActiveGuidanceIndex(0);
  }, [setupTarget]);

  useEffect(() => {
    if (setupPanels.length <= 1) return;
    const timer = window.setInterval(() => {
      setActiveGuidanceIndex((current) => (current + 1) % setupPanels.length);
    }, 4500);
    return () => window.clearInterval(timer);
  }, [setupPanels.length]);

  return (
    <div className="space-y-16">
      <section className="grid gap-10 rounded-3xl border border-border/60 bg-surface/90 p-10 shadow-xl shadow-primary/10 backdrop-blur lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <div className="space-y-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
            {content.hero.eyebrow}
          </span>
          <h1 className="text-balance text-4xl font-bold tracking-tight text-foreground sm:text-5xl">{content.hero.title}</h1>
          <p className="text-lg text-muted-foreground">{content.hero.description}</p>
          <div className="space-y-6">
            <Link
              href={content.hero.foundingHref}
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary underline-offset-4 transition hover:underline"
            >
              <span className="size-2 rounded-full bg-primary/70" aria-hidden="true" />
              {content.hero.note}
            </Link>
            <div className="inline-flex flex-col items-center gap-2">
              <Link
                href={content.hero.ownerHref}
                className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold btn-primary shadow-lg shadow-primary/30 transition hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                {content.hero.ownerCta}
              </Link>
              <p className="text-center text-xs text-muted-foreground/80">{content.hero.ownerCtaNote}</p>
            </div>
          </div>
        </div>
        <div className="space-y-4 rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/20 to-primary/5 p-6">
          <Image
            src={courtIntelLogoSrc}
            alt="Court Intelligence"
            width={1200}
            height={320}
            className="h-auto w-full max-w-[420px]"
          />
          <p className="text-sm text-muted-foreground">{content.hero.visualNote}</p>
        </div>
      </section>

      <section className="grid gap-6 rounded-3xl border border-border/60 bg-surface p-6 shadow-lg shadow-primary/5 sm:grid-cols-3 sm:p-8">
        {content.highlights.map((item) => (
          <div key={item.value} className="space-y-2 border-border/40 sm:border-r last:border-0 sm:pr-6 sm:last:pr-0">
            <p className="text-2xl font-bold text-foreground sm:text-3xl">{item.value}</p>
            <p className="text-sm text-muted-foreground sm:text-base">{item.label}</p>
          </div>
        ))}
      </section>

      <section className="space-y-8">
        <div className="space-y-3">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{content.whatYouGet.title}</h2>
          <p className="text-base text-muted-foreground sm:text-lg">{content.whatYouGet.description}</p>
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          {content.whatYouGet.items.map((item) => (
            <article
              key={item.title}
              className="flex h-full flex-col gap-4 rounded-2xl border border-border/60 bg-surface p-6 shadow-md shadow-primary/10"
            >
              <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {item.points.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <CheckIcon className="mt-0.5 size-4 flex-shrink-0 text-primary" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="space-y-6 rounded-3xl border border-border/60 bg-surface p-8 shadow-lg shadow-primary/10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-3">
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              {content.setupOutcomes.title}
            </h2>
            <p className="max-w-3xl text-base text-muted-foreground sm:text-lg">
              {content.setupOutcomes.description}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setGuideOpen(true)}
            className="inline-flex items-center justify-center rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {setupGuide.openGuideLabel}
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {([
            ['quick_estimate', setupGuide.outcomes.quickEstimate.label],
            ['court_availability', setupGuide.outcomes.courtAvailability.label],
            ['court_and_queue', setupGuide.outcomes.courtAndQueue.label],
          ] as const).map(([targetValue, label]) => {
            const isActive = setupTarget === targetValue;
            return (
              <button
                key={targetValue}
                type="button"
                onClick={() => setSetupTarget(targetValue)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  isActive
                    ? 'border border-primary bg-primary text-primary-foreground'
                    : 'border border-border bg-background text-foreground hover:border-primary/60'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div className="rounded-2xl border border-border/60 bg-background p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {selectedOutcome.unlocks.map((capability) => (
                <span
                  key={capability}
                  className="inline-flex items-center rounded-full border border-border/50 bg-surface px-3 py-1 text-xs font-medium text-foreground"
                >
                  {capability}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              {setupPanels.map((panel, index) => {
                const isActive = index === activeGuidanceIndex;
                return (
                  <button
                    key={panel.key}
                    type="button"
                    aria-label={panel.label}
                    onClick={() => setActiveGuidanceIndex(index)}
                    className={`h-2.5 w-2.5 rounded-full transition ${
                      isActive ? 'bg-primary' : 'bg-border hover:bg-primary/50'
                    }`}
                  />
                );
              })}
            </div>
          </div>

          {activePanel ? (
            <div className="mt-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {activePanel.label}
              </p>
              <p className="mt-2 max-w-4xl text-base text-foreground">{activePanel.value}</p>
            </div>
          ) : null}
        </div>
      </section>

      <section className="grid gap-8 rounded-3xl border border-border/60 bg-surface/90 p-8 shadow-xl shadow-primary/10 lg:grid-cols-2">
        <article className="space-y-5 rounded-2xl border border-border/60 bg-background p-6 shadow-sm">
          <h2 className="text-2xl font-semibold text-foreground">{content.workflow.title}</h2>
          <div className="space-y-4">
            {content.workflow.steps.map((step, index) => (
              <div key={step.title} className="flex items-start gap-3 rounded-xl bg-muted/30 px-3 py-3">
                <span className="inline-flex size-7 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary">
                  {index + 1}
                </span>
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-foreground">{step.title}</p>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </article>
        <article className="space-y-4 rounded-2xl border border-border/60 bg-background p-6 shadow-sm">
          <h2 className="text-2xl font-semibold text-foreground">{content.playerBenefit.title}</h2>
          <p className="text-sm text-muted-foreground">{content.playerBenefit.description}</p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {content.playerBenefit.points.map((point) => (
              <li key={point} className="flex items-start gap-3 rounded-xl bg-muted/30 px-3 py-3">
                <CheckIcon className="mt-0.5 size-4 flex-shrink-0 text-primary" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </article>
      </section>

      <section className="grid gap-6 rounded-3xl border border-border/60 bg-surface p-8 shadow-lg shadow-primary/10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <article className="space-y-4">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{playerAdvocacy.title}</h2>
          <p className="text-base text-muted-foreground sm:text-lg">{playerAdvocacy.description}</p>
          <ul className="space-y-3 text-sm text-muted-foreground">
            {playerAdvocacy.points.map((point) => (
              <li key={point} className="flex items-start gap-3 rounded-2xl border border-border/50 bg-background px-4 py-3">
                <CheckIcon className="mt-0.5 size-4 flex-shrink-0 text-primary" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </article>
        <article className="space-y-4 rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/15 to-background p-6">
          <p className="text-sm text-muted-foreground">{playerAdvocacy.description}</p>
          <div className="flex flex-wrap gap-3">
            <Link
              href={playerAdvocacyHref}
              className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold btn-primary shadow-md shadow-primary/30 transition hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {playerAdvocacy.primaryCta}
            </Link>
            <Link
              href={content.hero.foundingHref}
              className="inline-flex items-center justify-center rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {playerAdvocacy.secondaryCta}
            </Link>
          </div>
        </article>
      </section>

      <section className="space-y-6 rounded-3xl border border-border/60 bg-surface p-8 shadow-lg shadow-primary/10">
        <div className="space-y-3">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{content.privacy.title}</h2>
          <p className="text-base text-muted-foreground sm:text-lg">{content.privacy.description}</p>
        </div>
        <ul className="grid gap-3 text-sm text-muted-foreground">
          {content.privacy.points.map((point) => (
            <li key={point} className="flex items-start gap-3 rounded-xl border border-border/50 bg-muted/30 px-4 py-3">
              <CheckIcon className="mt-0.5 size-4 flex-shrink-0 text-primary" />
              <span>{point}</span>
            </li>
          ))}
        </ul>
        <p className="rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-foreground">
          {content.privacy.playerNote}
        </p>
        <Link
          href={content.privacy.policyHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {content.privacy.policyCta}
        </Link>
      </section>

      <section id="pricing" className="scroll-mt-24 space-y-8 rounded-3xl border border-border/60 bg-surface p-8 shadow-lg shadow-primary/10">
        <div className="space-y-3">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{content.pricing.title}</h2>
          <p className="text-base text-muted-foreground sm:text-lg">{content.pricing.description}</p>
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          {content.pricing.cards.map((card) => (
            <article
              key={card.name}
              className={`relative flex h-full flex-col gap-4 rounded-2xl border bg-background p-6 shadow-sm ${
                card.highlighted ? 'border-primary/50 ring-1 ring-primary/30' : 'border-border/60'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-xl font-semibold text-foreground">{card.name}</h3>
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">{card.price}</span>
              </div>
              <p className="text-sm text-muted-foreground">{card.description}</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {card.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <CheckIcon className="mt-0.5 size-4 flex-shrink-0 text-primary" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">{content.pricing.footnote}</p>
      </section>

      <section className="space-y-6 rounded-3xl border border-border/60 bg-surface px-6 py-10 shadow-lg shadow-primary/5 sm:px-10">
        <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{content.faq.title}</h2>
        <div className="grid gap-4 lg:grid-cols-2">
          {content.faq.items.map((item) => (
            <article key={item.question} className="space-y-2 rounded-2xl border border-border/50 bg-background p-5 shadow-sm">
              <h3 className="text-base font-semibold text-foreground">{item.question}</h3>
              <p className="text-sm text-muted-foreground">{item.answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="space-y-6 rounded-3xl border border-border/60 bg-surface px-6 py-10 text-center shadow-lg shadow-primary/10 sm:px-10">
        <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{content.finalCta.title}</h2>
        <p className="mx-auto max-w-3xl text-sm text-muted-foreground sm:text-base">{content.finalCta.description}</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            href={content.finalCta.primaryHref}
            className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold btn-primary shadow transition hover:-translate-y-0.5 hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {content.finalCta.primaryCta}
          </Link>
          <Link
            href={content.finalCta.secondaryHref}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {content.finalCta.secondaryCta}
          </Link>
        </div>
      </section>

      {guideOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="marketing-setup-guide-title"
        >
          <div className="w-full max-w-5xl rounded-3xl border border-border/70 bg-surface p-5 shadow-2xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 id="marketing-setup-guide-title" className="text-xl font-semibold text-foreground">
                  {setupGuide.modal.title}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">{setupGuide.subtitle}</p>
              </div>
              <button
                type="button"
                onClick={() => setGuideOpen(false)}
                className="rounded-full border border-border px-4 py-2 text-sm font-semibold text-foreground"
              >
                {setupGuide.modal.closeLabel}
              </button>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-3">
              {([
                {
                  title: setupGuide.outcomes.quickEstimate.label,
                  eyebrow: setupGuide.outcomes.quickEstimate.eyebrow,
                  description: setupGuide.outcomes.quickEstimate.description,
                  unlocksTitle: setupGuide.outcomes.quickEstimate.unlocksTitle,
                  unlocks: setupGuide.outcomes.quickEstimate.unlocks,
                  setupTitle: setupGuide.outcomes.quickEstimate.setupTitle,
                  setup: setupGuide.outcomes.quickEstimate.setup,
                },
                {
                  title: setupGuide.outcomes.courtAvailability.label,
                  eyebrow: setupGuide.outcomes.courtAvailability.eyebrow,
                  description: setupGuide.outcomes.courtAvailability.description,
                  unlocksTitle: setupGuide.outcomes.courtAvailability.unlocksTitle,
                  unlocks: setupGuide.outcomes.courtAvailability.unlocks,
                  setupTitle: setupGuide.outcomes.courtAvailability.setupTitle,
                  setup: setupGuide.outcomes.courtAvailability.setup,
                },
                {
                  title: setupGuide.outcomes.courtAndQueue.label,
                  eyebrow: setupGuide.outcomes.courtAndQueue.eyebrow,
                  description: setupGuide.outcomes.courtAndQueue.description,
                  unlocksTitle: setupGuide.outcomes.courtAndQueue.unlocksTitle,
                  unlocks: setupGuide.outcomes.courtAndQueue.unlocks,
                  setupTitle: setupGuide.outcomes.courtAndQueue.setupTitle,
                  setup: setupGuide.outcomes.courtAndQueue.setup,
                },
              ] as const).map((item) => (
                <article
                  key={item.title}
                  className="rounded-2xl border border-border/60 bg-background p-4 shadow-sm"
                >
                  <h3 className="text-lg font-semibold text-foreground">{item.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{item.eyebrow}</p>
                  <p className="mt-3 text-sm text-foreground">{item.description}</p>
                  <dl className="mt-4 space-y-4">
                    <div>
                      <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {item.unlocksTitle}
                      </dt>
                      <dd className="mt-1">
                        <ul className="space-y-1 text-sm text-foreground">
                          {item.unlocks.map((entry) => (
                            <li key={entry} className="flex items-start gap-2">
                              <CheckIcon className="mt-0.5 size-4 flex-shrink-0 text-primary" />
                              <span>{entry}</span>
                            </li>
                          ))}
                        </ul>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {item.setupTitle}
                      </dt>
                      <dd className="mt-1">
                        <ul className="space-y-1 text-sm text-foreground">
                          {item.setup.map((entry) => (
                            <li key={entry} className="flex items-start gap-2">
                              <CheckIcon className="mt-0.5 size-4 flex-shrink-0 text-primary" />
                              <span>{entry}</span>
                            </li>
                          ))}
                        </ul>
                      </dd>
                    </div>
                  </dl>
                </article>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
